import { Link2, Copy, Check } from 'lucide-react';
import { PageHeader } from '@/shared/ui';
import { useState } from 'react';
import { haptic } from '@/shared/utils/telegram';
import { PLATFORM_TEMPLATES } from './linksTemplates';

interface Platform {
  name: string;
  path: string;
  recommended?: boolean;
  description: string;
}

const PLATFORMS: Platform[] = [
  {
    name: 'Custom',
    path: '/postbacks/custom',
    recommended: true,
    description: 'Universal endpoint (recommended)',
  },
  { name: 'Referon', path: '/postbacks/referon', description: 'Referon affiliate network' },
  { name: 'Affise', path: '/postbacks/affise', description: 'Affise affiliate network' },
  {
    name: 'Sales Expert',
    path: '/postbacks/sales-expert',
    description: 'Sales Expert / Cellxpert',
  },
  { name: 'Traffickong', path: '/postbacks/traffickong', description: 'Traffickong (ui=clickid)' },
  { name: 'CPAround', path: '/postbacks/cparound', description: 'CPAround affiliate network' },
  { name: 'Keitaro', path: '/postbacks/keitaro', description: 'Keitaro tracker' },
  { name: 'Everflow', path: '/postbacks/everflow', description: 'Everflow platform' },
  { name: 'Affilka', path: '/postbacks/affilka', description: 'Affilka network' },
  { name: 'Cellxpert', path: '/postbacks/cellxpert', description: 'Cellxpert platform' },
];

const PARAM_EXAMPLES = [
  {
    param: 'brand',
    description: 'Brand identifier (required)',
    example: 'alfcasino',
    required: true,
  },
  {
    param: 'event',
    description: 'Event type: cpa, registration, ftd, redep',
    example: 'cpa',
    required: true,
  },
  { param: 'uid', description: 'Click ID / External user ID', example: '{clickid}' },
  { param: 'subid', description: 'Affiliate sub-ID for tracking', example: '{subid}' },
  {
    param: 'tracking_tag',
    description: 'Campaign tracking tag (e.g., WA_FR_DIM_20). Parsed: [Sender]_[Geo]_[SheetDate]',
    example: 'WA_FR_DIM_20',
  },
  {
    param: 'source',
    description: 'Traffic source / channel (sms, whatsapp, email, facebook)',
    example: 'sms',
  },
  {
    param: 'amount',
    description: 'Transaction amount (required for CPA revenue tracking)',
    example: '{payout}',
  },
  { param: 'currency', description: 'Currency code (USD, EUR, BRL, etc.)', example: 'EUR' },
  { param: 'country', description: 'ISO 3166-1 alpha-2 country code', example: 'US' },
  {
    param: 'sub2...sub5',
    description: 'Custom sub-parameters for extra tracking',
    example: '{subid2}',
  },
  { param: 'type', description: 'Postback type: registration, first_deposit, cpa', example: 'cpa' },
  {
    param: 'level',
    description: 'Tracking level: affiliate, brand, media_campaign, media_item',
    example: 'affiliate',
  },
  { param: 'affiliateId', description: 'Affiliate ID from partner network', example: 'AFF_789' },
  {
    param: 'transactionId',
    description: 'Partner transaction ID (for reconciliation)',
    example: 'TXN_ABC123',
  },
  {
    param: 'timestamp',
    description: 'Event timestamp (ISO 8601, unix ms, unix sec)',
    example: '2026-01-18T10:30:00Z',
  },
  { param: 'test', description: 'Mark as test event (excluded from stats)', example: 'true' },
];

export function LinksPage() {
  const [copiedTemplateIndex, setCopiedTemplateIndex] = useState<number | null>(null);
  const [selectedPlatform, setSelectedPlatform] = useState(PLATFORMS[0]);
  // Legacy by default: this page's templates get copied into partner panels by muscle memory,
  // and the format someone copies should not change under them. The new one is one tap away.
  const [withBrandId, setWithBrandId] = useState(false);

  // Get API URL from environment variable (set in .env.production.local)
  const baseUrl = import.meta.env.VITE_API_BASE_URL || 'https://api.kentozo.xyz';

  const templates = PLATFORM_TEMPLATES[selectedPlatform.path];

  const copyTemplate = async (text: string, index: number) => {
    try {
      await navigator.clipboard.writeText(text);
      haptic.impact('light');
      setCopiedTemplateIndex(index);
      setTimeout(() => setCopiedTemplateIndex(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="flex flex-col gap-4 animate-in fade-in slide-in-from-top-4 duration-500 pb-20">
      <PageHeader title="Tracking Links" />

      {/* Info Card */}
      <div className="p-4 rounded-2xl bg-blue-500/10 border border-blue-500/20">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-blue-500/20">
            <Link2 size={20} className="text-blue-600" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-blue-600 mb-1">Postback URL Builder</h3>
            <p className="text-xs text-blue-600/80">
              Copy ready-to-use tracking URLs with platform-specific macros. Paste to your affiliate
              network and replace {'{'}
              <strong>brand</strong>
              {'}'} with your brand name.
            </p>
          </div>
        </div>
      </div>

      {/* Platform Selector */}
      <div className="flex flex-col gap-2">
        <h3 className="text-xs font-black text-[var(--tg-theme-hint-color)] uppercase tracking-widest px-1">
          Select Platform
        </h3>
        <div className="grid grid-cols-2 gap-2">
          {PLATFORMS.map((platform) => (
            <button
              key={platform.path}
              onClick={() => {
                setSelectedPlatform(platform);
                setCopiedTemplateIndex(null);
                haptic.impact('light');
              }}
              className={`p-3 rounded-xl border transition-all ${
                selectedPlatform.path === platform.path
                  ? 'bg-[var(--tg-theme-link-color)]/20 border-[var(--tg-theme-link-color)] font-bold'
                  : 'bg-[var(--tg-theme-secondary-bg-color)] border-black/5 dark:border-white/5'
              }`}
            >
              <div className="text-left">
                <div className="text-xs font-bold flex items-center gap-1">
                  {platform.name}
                  {platform.recommended && (
                    <span className="text-[8px] bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 px-1 py-0.5 rounded">
                      REC
                    </span>
                  )}
                </div>
                <div className="text-[9px] text-[var(--tg-theme-hint-color)] mt-0.5">
                  {platform.description}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Link format */}
      <div className="flex flex-col gap-2">
        <h3 className="text-xs font-black text-[var(--tg-theme-hint-color)] uppercase tracking-widest px-1">
          Link Format
        </h3>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => {
              setWithBrandId(false);
              setCopiedTemplateIndex(null);
              haptic.impact('light');
            }}
            className={`p-3 rounded-xl border text-left transition-all ${
              !withBrandId
                ? 'bg-[var(--tg-theme-link-color)]/20 border-[var(--tg-theme-link-color)]'
                : 'bg-[var(--tg-theme-secondary-bg-color)] border-black/5 dark:border-white/5'
            }`}
          >
            <div className="text-xs font-bold">brand</div>
            <div className="text-[9px] text-[var(--tg-theme-hint-color)] mt-0.5">
              Legacy — what panels already have
            </div>
          </button>
          <button
            onClick={() => {
              setWithBrandId(true);
              setCopiedTemplateIndex(null);
              haptic.impact('light');
            }}
            className={`p-3 rounded-xl border text-left transition-all ${
              withBrandId
                ? 'bg-[var(--tg-theme-link-color)]/20 border-[var(--tg-theme-link-color)]'
                : 'bg-[var(--tg-theme-secondary-bg-color)] border-black/5 dark:border-white/5'
            }`}
          >
            <div className="text-xs font-bold flex items-center gap-1">
              brand_id + brand
              <span className="text-[8px] bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 px-1 py-0.5 rounded">
                NEW
              </span>
            </div>
            <div className="text-[9px] text-[var(--tg-theme-hint-color)] mt-0.5">
              Exact match, for ambiguous names
            </div>
          </button>
        </div>
        <div className="p-3 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] border border-black/5 dark:border-white/5">
          <p className="text-[10px] text-[var(--tg-theme-hint-color)]">
            Both work, and both keep <code>brand=</code> in the link. The id decides which brand an
            event belongs to; the name stays readable in the payload and in reports.
          </p>
          <p className="text-[10px] text-[var(--tg-theme-hint-color)] mt-1">
            Use <code>brand_id</code> where the name alone is ambiguous — one network account
            covering several casinos, or a partner that sends its own spelling. Everywhere else the
            legacy format is fine and nothing has to change.
          </p>
          {withBrandId && (
            <p className="text-[10px] text-[var(--tg-theme-hint-color)] mt-1">
              Replace <code>{'{brand_id}'}</code> with the brand&apos;s registry id — the Brands
              page shows it on the brand card.
            </p>
          )}
        </div>
      </div>

      {/* Event Templates - Ready to Copy */}
      <div className="flex flex-col gap-2">
        <h3 className="text-xs font-black text-[var(--tg-theme-hint-color)] uppercase tracking-widest px-1">
          Ready-to-Use Templates
        </h3>
        <div className="p-3 rounded-xl bg-blue-500/10 border border-blue-500/20 mb-1">
          <p className="text-[10px] text-blue-600 dark:text-blue-400">
            <strong>Platform:</strong> {selectedPlatform.name} — {selectedPlatform.description}
          </p>
        </div>
        {templates?.events.map((template, index) => {
          const query = withBrandId ? template.paramsWithBrandId : template.params;
          const fullUrl = `${baseUrl}${selectedPlatform.path}?${query}`;
          const isCopied = copiedTemplateIndex === index;

          return (
            <div
              key={template.event}
              className="p-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/20"
            >
              <div className="flex items-center justify-between mb-2">
                <div>
                  <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
                    {template.name}
                  </span>
                  <p className="text-[10px] text-emerald-600/70 dark:text-emerald-400/70 mt-0.5">
                    {template.description}
                  </p>
                </div>
                <button
                  onClick={() => copyTemplate(fullUrl, index)}
                  className="p-2 rounded-lg bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 active:scale-95 transition-transform flex-shrink-0"
                >
                  {isCopied ? <Check size={16} /> : <Copy size={16} />}
                </button>
              </div>
              <code className="text-[9px] text-emerald-600/80 dark:text-emerald-400/80 font-mono break-all block">
                {fullUrl}
              </code>
            </div>
          );
        })}

        {/* Platform-specific notes */}
        {templates?.notes && templates.notes.length > 0 && (
          <div className="p-3 rounded-xl bg-indigo-500/10 border border-indigo-500/20">
            {templates.notes.map((note, i) => (
              <p
                key={i}
                className="text-[10px] text-indigo-600 dark:text-indigo-400 mt-0.5 first:mt-0"
              >
                {note}
              </p>
            ))}
          </div>
        )}

        <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
          <p className="text-[10px] text-amber-600 dark:text-amber-400">
            <strong>Replace</strong> <code>{'{brand}'}</code> with your brand name (e.g., alfcasino,
            millioner)
          </p>
          <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-1">
            <strong>Optional:</strong> Add <code>&source=sms</code> for traffic source tracking
            (sms, whatsapp, email, etc.)
          </p>
        </div>
      </div>

      {/* Parameters Guide */}
      <div className="flex flex-col gap-2">
        <h3 className="text-xs font-black text-[var(--tg-theme-hint-color)] uppercase tracking-widest px-1">
          Available Parameters
        </h3>
        <div className="p-4 rounded-2xl bg-[var(--tg-theme-secondary-bg-color)] border border-black/5 dark:border-white/5 space-y-3">
          {PARAM_EXAMPLES.map((param) => (
            <div key={param.param} className="space-y-1">
              <div className="flex items-center gap-2">
                <code className="text-xs font-bold text-[var(--tg-theme-link-color)]">
                  {param.param}
                </code>
                {param.required ? (
                  <span className="text-[8px] font-black text-rose-500 bg-rose-500/10 px-1.5 py-0.5 rounded">
                    REQUIRED
                  </span>
                ) : null}
              </div>
              <p className="text-[11px] text-[var(--tg-theme-hint-color)]">{param.description}</p>
              <code className="text-[10px] text-emerald-600 dark:text-emerald-400 font-mono">
                {param.param}={param.example}
              </code>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
